import React, { useState } from 'react';
import { ReactEditor, useSlate, useSelected, useReadOnly } from 'slate-react';
import { type Element, Transforms } from 'slate';
import type {
  ComponentProps,
  EditorPlugin,
  EmbedElement,
} from '../../../types/aha-editor';
import Resizer from '../../../components/Resizer/Resizer';
import ToolDelete from '../../../tools/ToolDelete';
import { normalizeHref } from '../../LinkPlugin/utils';
import {
  ToolEmbedDisplayLink,
  ToolEmbedDisplayFrame,
} from '../tools/ToolEmbedDisplay';
import ToolEditSrc from '../tools/ToolEditSrc';
import ToolEditSize from '../tools/ToolEditSize';
import ToolEditArtifactEmbed from '../tools/ToolEditArtifactEmbed';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import EmbedCommon from '../EmbedCommon';
import styles from './EmbedComponentPlugin.module.css';
import { isEmbedElement } from '../../../types/guards';

// Render an embedded element.
const Embed: React.FC<ComponentProps<EmbedElement>> = ({ element }) => {
  const editor = useSlate();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const [allowInteraction, setAllowInteraction] = useState(false);

  if (!isEmbedElement(element)) return null;

  const { embed_src, embed_display, resizer_originalWidth } = element;

  // When the element is not selected then we disable interaction with the element.
  if (allowInteraction && !selected && !readOnly) {
    setAllowInteraction(false);
  } else if (!allowInteraction && readOnly) {
    // Allow interaction directly when the element can't be selected.
    setAllowInteraction(true);
  }

  const selectNode = (event: React.MouseEvent) => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.select(editor, { path: [...path, 0], offset: 0 });
    event.preventDefault();
  };

  const embedType = EmbedCommon.embedTypeForUrl(embed_src);
  if (!embedType) {
    return (
      <span
        style={{
          padding: 4,
          backgroundColor: 'var(--theme-tertiary-background)',
          borderRadius: 4,
        }}
      >
        Embed URL not supported
      </span>
    );
  }

  if (embed_display === 'link') {
    return <a href={normalizeHref(embed_src)}>{embed_src}</a>;
  }

  /*
   * Overlay element to it possible to select this element by clicking.
   * This will mask the controls until the element is selected.
   */
  return (
    <SelectionRectangle>
      <Resizer
        width={element.resizer_width}
        height={element.resizer_height}
        originalWidth={element.resizer_originalWidth}
        originalHeight={element.resizer_originalHeight}
        element={element}
        handles={
          !allowInteraction
            ? embedType.getProportionalResize()
              ? ['e']
              : ['se', 's', 'e']
            : []
        }
        keepRatio={embedType.getProportionalResize()}
        useAutoHeight={false}
        showPixelSize
        liveResize
      >
        <iframe
          style={{
            width: resizer_originalWidth,
            maxWidth: '100%',
            height: '100%',
            border: 'none',
            boxShadow: '0 0 10px #ddd',
          }}
          {...embedType.getIframeAttributes(embed_src)}
          onClick={selectNode}
        />
        {!allowInteraction && (
          <span
            contentEditable={false}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'end',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            onDoubleClick={() => setAllowInteraction(true)}
          >
            <span
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '60%',
                height: '30px',
                borderRadius: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                fontSize: 12,
                marginBottom: 20,
              }}
            >
              Double click to interact
            </span>
          </span>
        )}
      </Resizer>
    </SelectionRectangle>
  );
};

interface ComponentWrapperProps {
  attributes: Record<string, unknown>;
  element: Element;
  children?: React.ReactNode;
}

const withEmbedPosition = <P extends ComponentWrapperProps>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const ComponentWrapper: React.FC<P> = (props) => {
    const { attributes, children } = props;

    return (
      <span className={styles.embedInline} {...attributes}>
        <Component {...props} />
        {children}
      </span>
    );
  };
  return ComponentWrapper;
};

const EmbedComponentPlugin: EditorPlugin = {
  name: 'Embed Component Plugin',
  component: {
    type: 'embed',
    component: withEmbedPosition(Embed),
    toolbarItems: [
      [ToolEmbedDisplayFrame, ToolEmbedDisplayLink],
      [ToolEditArtifactEmbed],
      [ToolEditSize],
      [ToolEditSrc],
      [ToolDelete],
    ],
  },
};

export default EmbedComponentPlugin;
